import { useMemo, useState } from 'react';
import { Info, MapPin } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useWarehouseLayout } from '../../warehouse/model/warehouse.queries.js';
import { CellCardDialog } from '../../warehouse/ui/CellCardDialog.js';
import { cellPlaceText } from './CellPicker.js';

export interface ReservedCellsProps {
  warehouseId: string;
  /** Tanlangan mahsulot. `null` — hech narsa chizilmaydi. */
  nomenclatureId: string | null;
  /** Satrda HOZIR turgan katak kodi — u ro'yxatda tanlangan bo'lib turadi. */
  selectedCode: string;
  onPick: (code: string) => void;
  className?: string;
}

/** Ro'yxat shuncha katakdan oshmaydi — undan ko'pi qatorga sig'maydi. */
const MAX_CELLS = 8;

/**
 * "BU MAHSULOT QAYSI O'RINGA KETADI?" — javob KO'RINIB TURADI.
 *
 * Omborda bu qoida javonning o'zida, yozuv bilan turadi: eski skladchi
 * uni yoddan biladi, yangi kelgani esa javonlar orasida kod qidirib
 * yuradi.
 *
 * Ilgari javob katak kodi maydonining ochiladigan ro'yxatida edi va u
 * YASHIRIN edi: maydonga bosmagunicha ko'rinmasdi, ya'ni javob borligini
 * bilgan odamgina topardi. Endi u mahsulot tanlangan zahoti satrning
 * ostida, o'qiladigan qatorda turadi.
 *
 * Har o'rin — bosiladigan tugma: bosilsa kod maydonga tushadi. Yonidagi
 * belgi esa katak kartochkasini ochadi — "tanlashdan oldin ko'rib olay,
 * u yerda nima turibdi" degan boshqa niyat.
 *
 * Ajratilgan o'rin bo'lmasa — HECH NARSA chizilmaydi. "Ajratilgan o'rin
 * yo'q" degan yozuv har satrda takrorlanib, foydasiz shovqin bo'lardi.
 */
export const ReservedCells = ({
  warehouseId,
  nomenclatureId,
  selectedCode,
  onPick,
  className,
}: ReservedCellsProps) => {
  const [inspected, setInspected] = useState<string | null>(null);

  /* Daraxt `by-code` bilan BIR XIL ruxsat va doirani talab qiladi
     (`warehouse.read`) — ya'ni ro'yxatda odam ocholmaydigan katak paydo
     bo'lmaydi. Yangi endpoint ham kerak emas. */
  const layout = useWarehouseLayout(
    nomenclatureId === null || warehouseId === '' ? null : warehouseId,
  );

  const cells = useMemo(() => {
    if (nomenclatureId === null) return [];
    const found: { id: string; code: string; level: number; idx: number; blocked: boolean }[] = [];
    for (const zone of layout.data?.zones ?? []) {
      for (const rack of zone.racks) {
        for (const cell of rack.cells) {
          if (!cell.reservedFor.some((item) => item.id === nomenclatureId)) continue;
          found.push({
            id: cell.id,
            code: cell.code,
            level: cell.level,
            idx: cell.idx,
            blocked: cell.status === 'BLOCKED',
          });
        }
      }
    }
    return found.sort((a, b) => a.code.localeCompare(b.code)).slice(0, MAX_CELLS);
  }, [layout.data, nomenclatureId]);

  if (cells.length === 0) return null;

  const chosen = selectedCode.trim().toUpperCase();

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1.5', className)}>
      <span className="text-accent flex shrink-0 items-center gap-1.5 text-xs">
        <MapPin size={13} aria-hidden />
        {t('stock.reservedCellsLabel')}
      </span>

      <ul className="flex flex-wrap items-center gap-1.5">
        {cells.map((cell) => {
          const active = cell.code === chosen;
          const place = cellPlaceText(cell);
          return (
            <li
              key={cell.id}
              className={cn(
                'flex items-center gap-1 rounded-[var(--radius-pill)] border',
                'transition-colors duration-[var(--motion-fast)]',
                active
                  ? 'border-accent bg-accent-soft'
                  : 'border-border bg-surface-2 hover:border-border-strong',
              )}
            >
              <button
                type="button"
                aria-pressed={active}
                /* Nom AYTILADI: ko'z bilan bu belgi, ekran o'quvchi uchun
                   esa amal — "shu o'rinni tanlash". Yonidagi kartochka
                   tugmasi bilan adashmasligi ham shundan. */
                aria-label={t('stock.reservedCellPick', { code: cell.code })}
                onClick={() => onPick(cell.code)}
                /* Manzil TUGMANING ICHIDA: "A-01-01-01" ni o'qigan odam
                   uning qayerdaligini bilmaydi, javon oldiga borish
                   uchun esa aynan shu kerak. */
                className="flex items-center gap-2 py-1 pr-1 pl-2.5 text-xs"
              >
                <span className={cn('tabular font-medium', active ? 'text-accent' : 'text-text')}>
                  {cell.code}
                </span>
                {place === null ? null : (
                  <span className="text-text-faint hidden sm:inline">{place}</span>
                )}
                {/* Bloklangan o'rin ham ro'yxatda QOLADI: ajratish kuchda
                    va odam nega ishlatolmayotganini bilishi kerak. */}
                {cell.blocked ? (
                  <span className="text-warn">{t('stock.cellSuggestBlocked')}</span>
                ) : null}
              </button>

              <button
                type="button"
                aria-label={t('stock.cellSuggestInspect', { code: cell.code })}
                onClick={() => setInspected(cell.id)}
                className={cn(
                  'text-text-faint mr-0.5 flex size-5 shrink-0 items-center justify-center',
                  'rounded-full transition-colors duration-[var(--motion-fast)]',
                  'hover:bg-surface hover:text-text',
                )}
              >
                <Info size={12} aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>

      <CellCardDialog
        warehouseId={warehouseId}
        cellId={inspected}
        onClose={() => setInspected(null)}
        onUse={(code) => {
          onPick(code);
          setInspected(null);
        }}
      />
    </div>
  );
};
